import styled from "styled-components";
import { MainInput, MainLabel } from "../../styles/common/input.styled"
import type { Ref } from "react";
import { useId, useState } from "react"

interface LabelInputProps {
    label?: string;
    placeholder?: string;
    name?: string;
    type?: string;
    value?: string;
    focusColor?: string;
    inputRef?: Ref<HTMLInputElement>;
    autoComplete?: string;
    preventAutofill?: boolean;
    showPasswordToggle?: boolean;
    trailingContent?: React.ReactNode;
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const LoginField = styled.div<{ $focusColor?: string; $focused: boolean }>`
    position: relative;
    width: 100%;
    height: 60px;
    border-bottom: 1px solid ${({ $focusColor, $focused, theme }) => $focused
        ? $focusColor || theme.color.point_y_100
        : theme.color.primary_300};
    transition: border-color 0.16s ease;

    &:focus-within {
        border-bottom-color: ${({ $focusColor, theme }) => $focusColor || theme.color.point_y_100};
    }
`;

const FloatingLabel = styled(MainLabel)<{ $floating: boolean }>`
    position: absolute;
    top: ${({ $floating }) => $floating ? '7px' : '50%'};
    left: 0;
    z-index: 1;
    transform: translateY(${({ $floating }) => $floating ? '0' : '-50%'});
    color: ${({ $floating, theme }) => $floating ? theme.color.text_color_400 : theme.color.text_color_200};
    font-size: ${({ $floating }) => $floating ? '11px' : '14px'};
    font-weight: ${({ $floating }) => $floating ? 600 : 500};
    line-height: 1;
    pointer-events: none;
    transition: top 0.16s ease, transform 0.16s ease, color 0.16s ease, font-size 0.16s ease;
`;

const FloatingInput = styled(MainInput)<{ $hasTrailingContent?: boolean }>`
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 37px;
    padding: 7px ${({ $hasTrailingContent }) => $hasTrailingContent ? '136px' : '32px'} 8px 0;
    border: 0;
    border-radius: 0;
    background: transparent;
    box-shadow: none;

    &:focus {
        border: 0;
        box-shadow: none;
    }

    &::placeholder {
        color: transparent;
    }
`;

const VisibilityButton = styled.button`
    position: absolute;
    right: 0;
    bottom: 5px;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 4px;
    border: 0;
    background: transparent;
    color: ${({ theme }) => theme.color.text_color_400};
    cursor: pointer;

    &:hover {
        color: ${({ theme }) => theme.color.text_color_200};
    }

    &:focus-visible {
        outline: 2px solid ${({ theme }) => theme.color.point_y_100};
        outline-offset: 1px;
        border-radius: 4px;
    }
`;

const VisibilityIcon = ({ visible }: { visible: boolean }) => (
    <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12Z" />
        {visible ? <circle cx="12" cy="12" r="2.5" /> : <line x1="3" y1="3" x2="21" y2="21" />}
    </svg>
);

const LabelInput: React.FC<LabelInputProps> = (
    {
        label, placeholder, name, type, value, focusColor, inputRef, autoComplete, preventAutofill, showPasswordToggle, trailingContent, onBlur, onChange
    }
) => {

    const id = useId();
    const [isFocused, setIsFocused] = useState(false);
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);
    const isFloating = isFocused || Boolean(value);
    const isPasswordInput = type === 'password';
    const inputType = isPasswordInput && isPasswordVisible ? 'text' : type;

    return (
        <LoginField $focusColor={focusColor} $focused={isFocused}>
            <FloatingLabel $floating={isFloating} htmlFor={id}>{label}</FloatingLabel>
            <FloatingInput
                name={name}
                type={inputType}
                id={id}
                ref={inputRef}
                $hasTrailingContent={Boolean(trailingContent)}
                placeholder={placeholder}
                value={value}
                autoComplete={autoComplete}
                aria-autocomplete={preventAutofill ? 'none' : undefined}
                data-1p-ignore={preventAutofill ? 'true' : undefined}
                data-lpignore={preventAutofill ? 'true' : undefined}
                data-bwignore={preventAutofill ? 'true' : undefined}
                data-protonpass-ignore={preventAutofill ? 'true' : undefined}
                data-form-type={preventAutofill ? 'other' : undefined}
                onChange={onChange}
                onFocus={() => setIsFocused(true)}
                onBlur={(event) => {
                    setIsFocused(false);
                    onBlur?.(event);
                }}
            />
            {showPasswordToggle && isPasswordInput && (
                <VisibilityButton
                    type="button"
                    aria-label={isPasswordVisible ? '비밀번호 숨기기' : '비밀번호 표시'}
                    aria-pressed={isPasswordVisible}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => setIsPasswordVisible((visible) => !visible)}
                >
                    <VisibilityIcon visible={isPasswordVisible} />
                </VisibilityButton>
            )}
            {trailingContent}
        </LoginField>
    )
}

export default LabelInput;
